import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import './consultas_ia.css';
import './usuarios_api.css';
import './principal.css';
import { EtiquetaDashboard } from './dashboardNawaiam';

// Sección Empresas de un ambiente: las cuentas empresa de NAWI Office con el check "Dar de alta esta empresa en la API".
// Habilitada, el integrador (Nawaiam) ve por la API las personas de su panel y puede preguntar sobre ellas. Las
// personas se siguen administrando en el panel de Office; en preproducción se copian al habilitarla y con "Sincronizar".
const CAMPOS = 'id nombre email dashboard personas apiEstado apiUsuarioId ultimaSync impedimento';
const LISTA = gql`query L($entorno: String) { getEmpresasApi(entorno: $entorno) { error errorMensaje items { ${CAMPOS} } } }`;
const FICHA = gql`query F($entorno: String, $id: ID!) { getEmpresaApi(entorno: $entorno, id: $id) {
  empresa { ${CAMPOS} } copiadasEnApi copia solicitante
  personas { id nombre email dob emailValidacion origen seCopia motivo enApi } } }`;
const SYNC = 'copiadas actualizadas reactivadas quitadas omitidas { nombre email motivo } en';
const HABILITAR = gql`mutation H($entorno: String, $id: ID!, $solicitante: String!) { habilitarEmpresaApi(entorno: $entorno, id: $id, solicitante: $solicitante) {
  id email estado mailEnviado mailError sincronizacion { ${SYNC} } } }`;
const SUSPENDER = gql`mutation S($entorno: String, $id: ID!) { suspenderEmpresaApi(entorno: $entorno, id: $id) { id estado } }`;
const SINCRONIZAR = gql`mutation Y($entorno: String, $id: ID!) { sincronizarEmpresaApi(entorno: $entorno, id: $id) { ${SYNC} } }`;

const fecha = (v) => (v ? new Date(v).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const nacimiento = (v) => { const m = /^(\d{2})(\d{2})(\d{4})$/.exec(v || ''); if (m) return `${m[1]}/${m[2]}/${m[3]}`; const i = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || ''); return i ? `${i[3]}/${i[2]}/${i[1]}` : 'Sin fecha'; };
const mensaje = (e) => e.graphQLErrors?.[0]?.message || e.message;
const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;
const resumenSync = (x) => {
  const partes = [];
  if (x.copiadas) partes.push(plural(x.copiadas, 'persona copiada', 'personas copiadas'));
  if (x.actualizadas) partes.push(plural(x.actualizadas, 'actualizada', 'actualizadas'));
  if (x.reactivadas) partes.push(plural(x.reactivadas, 'reactivada', 'reactivadas'));
  if (x.quitadas) partes.push(plural(x.quitadas, 'quitada', 'quitadas'));
  const base = partes.length ? partes.join(', ') : 'sin cambios';
  return x.omitidas.length ? `${base}. No se copiaron ${x.omitidas.length}: ${x.omitidas.map((o) => `${o.nombre} (${o.motivo})`).join('; ')}` : base;
};
const ESTADOS = { activo: ['Habilitada', 'ua-marca--ok'], suspendido: ['Suspendida', 'ua-marca--alerta'] };

export default function EmpresasApi({ entorno }) {
  const { data, loading, error, refetch } = useQuery(LISTA, { variables: { entorno }, fetchPolicy: 'network-only' });
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState('todas');
  const [abierta, setAbierta] = useState(null);
  const r = data?.getEmpresasApi;
  const xs = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return (r?.items || []).filter((e) => (!t || [e.nombre, e.email, e.dashboard].some((v) => (v || '').toLowerCase().includes(t)))
      && (filtro === 'todas' || (filtro === 'api' ? !!e.apiEstado : !e.apiEstado)));
  }, [r, busca, filtro]);
  const habilitadas = (r?.items || []).filter((e) => e.apiEstado === 'activo').length;

  return (
    <div className="ua">
      <p className="ua-intro">
        Las empresas dadas de alta en NAWI Office. Marcá <b>Dar de alta esta empresa en la API</b> para que el integrador la vea en su app y pueda
        preguntar sobre las personas de su panel. Las personas se siguen administrando en el panel de Office.
      </p>
      {error && <div className="cs-aviso cs-aviso--error">{mensaje(error)}</div>}
      {r?.error && <div className="cs-aviso cs-aviso--error">{r.errorMensaje}</div>}
      <div className="ua-barra">
        <input className="manual-input ua-busca" placeholder="Buscar por empresa, email o dashboard" value={busca} onChange={(e) => setBusca(e.target.value)} />
        <div className="ua-filtros">
          {[['todas', 'Todas'], ['api', 'En la API'], ['no', 'Sin alta en la API']].map(([k, t]) => (
            <button key={k} className={`ua-filtro ${filtro === k ? 'ua-filtro--activo' : ''}`} onClick={() => setFiltro(k)}>{t}</button>
          ))}
        </div>
      </div>
      <div className="cia-nota" style={{ marginBottom: 8 }}>{loading ? 'Cargando…' : `${plural(xs.length, 'empresa', 'empresas')} · ${plural(habilitadas, 'habilitada', 'habilitadas')} en la API`}</div>
      {abierta && <FichaEmpresa key={abierta} entorno={entorno} id={abierta} onCambio={refetch} onCerrar={() => setAbierta(null)} />}
      {xs.length > 0 && (
        <div className="panel-card">
          <div className="cia-tabla-scroll">
            <table className="cia-tabla">
              <thead><tr><th>Empresa</th><th>Email de la cuenta</th><th>Dashboard Nawaiam</th><th className="num">Personas</th><th>En la API</th><th>Última sincronización</th></tr></thead>
              <tbody>
                {xs.map((e) => (
                  <tr key={e.id} tabIndex={0} className={abierta === e.id ? 'cia-fila--elegida' : ''}
                    onClick={() => { setAbierta(e.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    onKeyDown={(ev) => { if (ev.key === 'Enter') setAbierta(e.id); }}>
                    <td><b>{e.nombre}</b></td>
                    <td>{e.email}</td>
                    <td><EtiquetaDashboard nombre={e.dashboard} compacta /></td>
                    <td className="num">{e.personas}</td>
                    <td>{e.apiEstado ? <span className={`ua-marca ${ESTADOS[e.apiEstado][1]}`}>{ESTADOS[e.apiEstado][0]}</span>
                      : e.impedimento ? <span className="cia-nota" title={e.impedimento}>No se puede</span> : <span className="cia-nota">—</span>}</td>
                    <td>{e.ultimaSync ? fecha(e.ultimaSync) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// Cuentas empresa de Office que todavía no están habilitadas en la API, para darlas de alta desde Usuarios API
// (26/10/2026). Cada una abre su ficha: el check, el solicitante y las personas de su panel.
const A_LA_VISTA = 12;
export function CuentasOffice({ entorno, onCambio }) {
  const { data, loading, error, refetch } = useQuery(LISTA, { variables: { entorno }, fetchPolicy: 'network-only' });
  const [busca, setBusca] = useState('');
  const [abierta, setAbierta] = useState(null);
  const [todas, setTodas] = useState(false);
  const r = data?.getEmpresasApi;
  const sinAlta = useMemo(() => (r?.items || []).filter((e) => e.apiEstado !== 'activo'), [r]);
  const xs = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return sinAlta.filter((e) => !t || [e.nombre, e.email, e.dashboard].some((v) => (v || '').toLowerCase().includes(t)));
  }, [sinAlta, busca]);
  const visibles = todas || busca.trim() ? xs : xs.slice(0, A_LA_VISTA);

  return (
    <div className="panel-card ua-cuentas">
      <p className="cia-nota" style={{ marginBottom: 10 }}>
        Las cuentas empresa ya dadas de alta en NAWI Office que todavía no están en la API{loading ? '' : ` (${sinAlta.length})`}. Elegí una para darla
        de alta con su cuenta y las personas de su panel; el solicitante recibe un mail.
      </p>
      {error && <div className="cs-aviso cs-aviso--error">{mensaje(error)}</div>}
      {r?.error && <div className="cs-aviso cs-aviso--error">{r.errorMensaje}</div>}
      <input className="manual-input ua-busca" style={{ width: '100%' }} placeholder="Buscar por empresa, email o dashboard" value={busca} onChange={(e) => setBusca(e.target.value)} />
      {abierta && (
        <FichaEmpresa key={abierta} entorno={entorno} id={abierta}
          onCambio={() => { refetch(); onCambio(); }} onCerrar={() => setAbierta(null)} />
      )}
      {loading && !r && <div className="cia-nota">Cargando…</div>}
      {r && !xs.length && <div className="cia-nota" style={{ padding: '14px 4px' }}>{busca.trim() ? 'Ninguna cuenta sin alta coincide con la búsqueda.' : 'Todas las cuentas de Office ya están en la API.'}</div>}
      <ul className="ua-cuentas-lista">
        {visibles.map((e) => (
          <li key={e.id} className={`ua-cuenta ${abierta === e.id ? 'ua-cuenta--abierta' : ''}`}>
            <div className="ua-cuenta-datos">
              <b>{e.nombre}</b>
              <span className="cia-nota">{e.email} · {e.dashboard ? `Dashboard Nawaiam: ${e.dashboard}` : 'sin dashboard de Nawaiam'} · {plural(e.personas, 'persona', 'personas')} en el panel</span>
            </div>
            {e.impedimento
              ? <span className="ua-cuenta-traba" title={e.impedimento}>No se puede: {e.impedimento}</span>
              : <button className="btn-outline ua-cuenta-boton" onClick={() => { setAbierta(e.id); }}>
                  {e.apiEstado === 'suspendido' ? 'Volver a dar de alta' : 'Dar de alta en la API'}
                </button>}
          </li>
        ))}
      </ul>
      {!todas && !busca.trim() && xs.length > A_LA_VISTA && (
        <button className="btn-subtle ua-ver-mas" onClick={() => setTodas(true)}>Ver las {xs.length} cuentas</button>
      )}
    </div>
  );
}

export function FichaEmpresa({ entorno, id, onCambio, onCerrar }) {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useQuery(FICHA, { variables: { entorno, id }, fetchPolicy: 'network-only' });
  const [habilitar, { loading: habilitando }] = useMutation(HABILITAR);
  const [suspender, { loading: suspendiendo }] = useMutation(SUSPENDER);
  const [sincronizar, { loading: sincronizando }] = useMutation(SINCRONIZAR);
  const [pidiendo, setPidiendo] = useState(null);   // 'alta' | 'baja'
  const [solicitante, setSolicitante] = useState('');
  const [aviso, setAviso] = useState(null);
  const d = data?.getEmpresaApi;
  const e = d?.empresa;
  const activa = e?.apiEstado === 'activo';

  const correr = async (fn, texto) => {
    setAviso(null);
    try { const r = await fn(); setPidiendo(null); await refetch(); onCambio(); setAviso({ tipo: 'ok', texto: texto(r) }); }
    catch (err) { setAviso({ tipo: 'error', texto: mensaje(err) }); }
  };
  const marcar = () => {
    setAviso(null);
    if (activa) setPidiendo('baja');
    else { setSolicitante(d?.solicitante || ''); setPidiendo('alta'); }
  };

  return (
    <div className="panel-card ua-ficha">
      <div className="ua-ficha-cabeza">
        <div>
          <div className="detail-section-title">{e ? e.nombre : 'Empresa'}</div>
          {e && <div className="cia-nota">{e.email}</div>}
          {e && <div className="op-fila-dash" style={{ marginTop: 4 }}><EtiquetaDashboard nombre={e.dashboard} /></div>}
        </div>
        <div className="ua-botones" style={{ marginTop: 0 }}>
          {e && <button className="btn-outline" onClick={() => navigate(`/players/${id}`)}>Abrir su panel en Office</button>}
          <button className="btn-subtle" onClick={onCerrar}>Cerrar</button>
        </div>
      </div>
      {loading && <div className="cia-nota">Cargando…</div>}
      {error && <div className="cs-aviso cs-aviso--error">{mensaje(error)}</div>}
      {aviso && <div className={`cs-aviso ${aviso.tipo === 'error' ? 'cs-aviso--error' : 'cs-aviso--ok'}`}>{aviso.texto}</div>}
      {e && (
        <>
          <div className="cia-kpis ua-kpis">
            <div className="panel-card cia-kpi"><span className="cia-kpi-titulo">Personas en el panel</span><span className="cia-kpi-valor">{e.personas}</span></div>
            <div className="panel-card cia-kpi"><span className="cia-kpi-titulo">Visibles por la API</span><span className="cia-kpi-valor">{activa ? d.personas.filter((p) => p.enApi).length : 0}</span>
              <span className="cia-kpi-pie">{activa ? (d.copia ? `copiadas a este ambiente` : 'leídas del panel') : 'la empresa no está habilitada'}</span></div>
            <div className="panel-card cia-kpi"><span className="cia-kpi-titulo">Estado en la API</span>
              <span className="cia-kpi-valor ua-kpi-estado">{e.apiEstado ? ESTADOS[e.apiEstado][0] : 'Sin alta'}</span>
              <span className="cia-kpi-pie">{e.ultimaSync ? `sincronizada ${fecha(e.ultimaSync)}` : ''}</span></div>
          </div>

          <label className={`ua-check ${e.impedimento ? 'ua-check--trabado' : ''}`}>
            <input type="checkbox" checked={activa || pidiendo === 'alta'} disabled={!!e.impedimento || habilitando || suspendiendo} onChange={marcar} />
            <span><b>Dar de alta esta empresa en la API</b>
              <span className="cia-nota">{e.impedimento || (activa
                ? 'El integrador la ve en su app y puede preguntar sobre las personas de su panel.'
                : 'Hoy la API le responde que se contacte con el área comercial.')}</span></span>
          </label>

          {pidiendo === 'alta' && (
            <div className="cf-confirmar">
              <span>Al darla de alta {d.copia ? 'se copian su cuenta y las personas de su panel a este ambiente, y ' : ''}le avisamos al solicitante.</span>
              <input className="manual-input" type="email" placeholder="email del solicitante" value={solicitante} onChange={(ev) => setSolicitante(ev.target.value)} autoFocus />
              <div className="ua-botones">
                <button className="btn-outline" onClick={() => setPidiendo(null)} disabled={habilitando}>Cancelar</button>
                <button className="btn-green" disabled={habilitando || !solicitante.trim()}
                  onClick={() => correr(() => habilitar({ variables: { entorno, id, solicitante: solicitante.trim() } }), (r) => {
                    const x = r.data.habilitarEmpresaApi;
                    return `Listo: ${x.email} quedó habilitada en la API.${x.sincronizacion ? ` ${resumenSync(x.sincronizacion)}.` : ''} `
                      + (x.mailEnviado ? `Le avisamos a ${solicitante.trim()}.` : `El aviso al solicitante no salió${x.mailError ? ` (${x.mailError})` : ''}; se puede reenviar desde Usuarios API.`);
                  })}>{habilitando ? 'Dando de alta…' : 'Dar de alta'}</button>
              </div>
            </div>
          )}
          {pidiendo === 'baja' && (
            <div className="cf-confirmar">
              <span>¿Sacar a <b>{e.nombre}</b> de la API? La API le va a responder que se contacte con el área comercial. Sus personas y conversaciones no se borran.</span>
              <div className="ua-botones">
                <button className="btn-outline" onClick={() => setPidiendo(null)} disabled={suspendiendo}>Cancelar</button>
                <button className="btn-danger" disabled={suspendiendo}
                  onClick={() => correr(() => suspender({ variables: { entorno, id } }), () => 'Suspendida: la API ya no acepta el login de esta empresa.')}>{suspendiendo ? 'Suspendiendo…' : 'Sí, sacarla'}</button>
              </div>
            </div>
          )}

          {activa && d.copia && (
            <div className="ua-acciones">
              <button className="btn-outline" disabled={sincronizando}
                onClick={() => correr(() => sincronizar({ variables: { entorno, id } }), (r) => `Sincronizada: ${resumenSync(r.data.sincronizarEmpresaApi)}.`)}>
                {sincronizando ? 'Sincronizando…' : 'Sincronizar con el panel de Office'}</button>
              <span className="cia-nota">Copia a este ambiente los cambios del panel: personas nuevas, quitadas y fechas cargadas en Office.</span>
            </div>
          )}

          <div className="detail-section-title">Personas del panel</div>
          {!d.personas.length && <div className="cia-nota">El panel no tiene personas.</div>}
          <ul className="ua-panel-lista">
            {d.personas.map((p) => (
              <li key={p.id} className={!p.seCopia ? 'ua-persona--mala' : ''}>
                <div><b>{p.nombre}</b> <span className="cia-nota">{p.email || 'sin email'} · {nacimiento(p.dob)}</span></div>
                <div className="ua-etiquetas">
                  {p.enApi && <span className="ua-marca ua-marca--ok">En la API</span>}
                  {p.emailValidacion === 'pendiente' && <span className="ua-marca ua-marca--alerta">Email sin validar</span>}
                  {!p.seCopia && <span className="ua-motivo">No llega a la API: {p.motivo}</span>}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
